/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { useState, type ReactElement } from "react";
import { useAuth } from "../../../../auth/AuthContext";
import { Label } from "../../../../shared/components/ui/label";
import {
  extractVideoMetadata,
  validateVideoMetadata,
  type VideoClientMetadata,
  type VideoValidationResult,
} from "../../hooks/useVideoUpload";
import { VideoFilePreview } from "./VideoFilePreview";
import { VideoUploadProgress } from "./VideoUploadProgress";
import { VideoValidationWarnings } from "./VideoValidationWarnings";

interface VideoUploadZoneProps {
  label: string;
  assetKind: "FEATURE" | "TRAILER";
  submissionId: string | null;
  titleId?: string | null; // Title ID for admin mode uploads
  value: string | null;
  maxSizeGB: number;
  onChange: (assetId: string, videoUrl: string) => void;
  mode?: "submission" | "title"; // Which entity type we're uploading for
  assetId?: string; // Existing asset ID for replacement (admin mode)
}

interface UploadProgress {
  bytesUploaded: number;
  totalBytes: number;
  percentage: number;
}

interface MultipartPart {
  partNumber: number;
  uploadUrl: string;
  etag?: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Use multipart upload for files larger than 1GB
const MULTIPART_THRESHOLD = 1 * 1024 * 1024 * 1024; // 1GB
const PART_SIZE = 50 * 1024 * 1024; // 50MB per part
const CONCURRENT_UPLOADS = 4; // Upload 4 parts in parallel

/**
 * Video Upload Zone Component
 * Handles large video file uploads to R2 via presigned URLs
 * - Files < 1GB: Single PUT upload
 * - Files >= 1GB: Multipart upload for reliability and resumability
 */
export function VideoUploadZone({
  label,
  assetKind,
  submissionId,
  titleId,
  value,
  maxSizeGB,
  onChange,
  mode = "submission",
  assetId,
}: Readonly<VideoUploadZoneProps>): ReactElement {
  const { refreshAuth } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);

  // Video metadata and validation state (Phase 5B)
  const [videoMetadata, setVideoMetadata] = useState<VideoClientMetadata | null>(null);
  const [validation, setValidation] = useState<VideoValidationResult | null>(null);

  // Determine if we're in replacement mode
  const canReplace = mode === "title" && assetId && value && !value.startsWith("pending:");

  // Helper: Fetch with automatic token refresh on 401
  const fetchWithAuth = async (url: string, options: RequestInit): Promise<Response> => {
    let response = await fetch(url, options);

    if (response.status === 401) {
      const refreshed = await refreshAuth();
      if (!refreshed) throw new Error("Session expired. Please log in again.");

      await new Promise((resolve) => setTimeout(resolve, 100));
      const newToken = localStorage.getItem("lumi-access-token");
      if (!newToken) throw new Error("Failed to refresh token");

      const updatedOptions = {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${newToken}` },
      };
      response = await fetch(url, updatedOptions);
    }

    return response;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset validation state
    setVideoMetadata(null);
    setValidation(null);

    if (!file.type.startsWith("video/")) {
      setError("Please select a video file");
      return;
    }

    const maxBytes = maxSizeGB * 1024 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`Video must be smaller than ${maxSizeGB}GB`);
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Extract metadata and validate (Phase 5B - client-side validation)
    try {
      const metadata = await extractVideoMetadata(file);
      setVideoMetadata(metadata);

      const validationResult = validateVideoMetadata(metadata, {
        assetKind,
        fileSize: file.size,
        maxSizeGB,
      });
      setValidation(validationResult);

      // Set error if validation failed (blocking errors only)
      if (!validationResult.valid) {
        setError(validationResult.errors[0]);
      }
    } catch (extractionError) {
      // Metadata extraction failed - allow upload anyway (backend will validate)
      console.warn("Video metadata extraction failed, proceeding without client validation:", extractionError);
      // Don't set error - let user proceed with upload
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // If replacing, use replacement endpoint; otherwise normal upload
    if (isReplacing && assetId) {
      await handleReplaceVideo();
    } else if (submissionId || (mode === "title" && titleId)) {
      // Choose upload strategy based on file size
      if (selectedFile.size >= MULTIPART_THRESHOLD) {
        await handleMultipartUpload();
      } else {
        await handleSinglePutUpload();
      }
    }
  };

  /**
   * Handle video replacement (admin mode only)
   * Uses dedicated replace endpoint to preserve asset ID
   * Supports both single PUT (<1GB) and multipart (>=1GB) uploads
   */
  const handleReplaceVideo = async () => {
    if (!selectedFile || !assetId) return;

    const currentToken = localStorage.getItem("lumi-access-token");
    if (!currentToken) {
      setError("Please log in again");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Choose upload strategy based on file size
      if (selectedFile.size >= MULTIPART_THRESHOLD) {
        await handleMultipartReplaceUpload();
      } else {
        await handleSinglePutReplaceUpload();
      }

      // Reset replacement mode on success
      setIsReplacing(false);
      setShowReplaceModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Replacement upload failed");
      setUploading(false);
      setProgress(null);
    }
  };

  /**
   * Single PUT upload for replacement files < 1GB
   */
  const handleSinglePutReplaceUpload = async () => {
    if (!selectedFile || !assetId) return;

    const currentToken = localStorage.getItem("lumi-access-token");
    if (!currentToken) throw new Error("Not authenticated");

    // Get presigned URL for replacement
    const replaceEndpoint = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/admin/assets/${assetId}/replace-video`;

    const presignedResponse = await fetchWithAuth(replaceEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        contentType: selectedFile.type,
        // Include client-side extracted metadata for backend validation
        clientMetadata: videoMetadata ?? undefined,
      }),
    });

    if (!presignedResponse.ok) {
      const errorData = (await presignedResponse.json()) as { error?: string };
      throw new Error(errorData.error || "Failed to get replacement upload URL");
    }

    const { uploadUrl } = (await presignedResponse.json()) as {
      uploadUrl: string;
      assetId: string;
    };

    // Upload to R2 with progress
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          setProgress({
            bytesUploaded: e.loaded,
            totalBytes: e.total,
            percentage: (e.loaded / e.total) * 100,
          });
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", selectedFile.type);
      xhr.send(selectedFile);
    });

    // Confirm upload completion (asset ID remains same)
    await confirmUploadCompletion(assetId);

    // Note: Encoding must be triggered manually for replacements
    // Admin can review upload success before triggering encoding
  };

  /**
   * Multipart upload for replacement files >= 1GB
   * Supports large files up to 50GB for FEATURE videos
   */
  const handleMultipartReplaceUpload = async () => {
    if (!selectedFile || !assetId) return;

    const currentToken = localStorage.getItem("lumi-access-token");
    if (!currentToken) throw new Error("Not authenticated");

    const CHUNK_SIZE = 100 * 1024 * 1024; // 100MB chunks
    const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);

    // Step 1: Initialize multipart upload for replacement
    const initEndpoint = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/admin/assets/${assetId}/replace-video`;
    const initResponse = await fetchWithAuth(initEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        contentType: selectedFile.type,
        useMultipart: true,
        totalParts: totalChunks, // Required for backend to generate part URLs
        // Include client-side extracted metadata for backend validation
        clientMetadata: videoMetadata ?? undefined,
      }),
    });

    if (!initResponse.ok) {
      const errorData = (await initResponse.json()) as { error?: string };
      throw new Error(errorData.error || "Failed to initialize multipart replacement upload");
    }

    const { uploadId, key, partUrls } = (await initResponse.json()) as {
      uploadId: string;
      key: string;
      assetId: string;
      partUrls: Array<{ partNumber: number; uploadUrl: string; expiresAt: string }>;
    };

    if (partUrls?.length !== totalChunks) {
      throw new Error(`Expected ${totalChunks} part URLs, got ${partUrls?.length || 0}`);
    }

    // Step 2: Upload chunks with presigned URLs
    const uploadedParts: Array<{ partNumber: number; etag: string }> = [];
    let uploadedBytes = 0;

    for (let i = 0; i < totalChunks; i++) {
      const partNumber = i + 1;
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
      const chunk = selectedFile.slice(start, end);

      // Find presigned URL for this part
      const partUrl = partUrls.find((p) => p.partNumber === partNumber)?.uploadUrl;
      if (!partUrl) {
        throw new Error(`Part URL not found for part ${partNumber}`);
      }

      // Upload this part directly to R2
      const partResponse = await fetch(partUrl, {
        method: "PUT",
        body: chunk,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!partResponse.ok) {
        throw new Error(`Failed to upload part ${partNumber}: ${partResponse.statusText}`);
      }

      const etag = partResponse.headers.get("etag");
      if (!etag) {
        throw new Error(`Missing ETag for part ${partNumber}`);
      }

      // Using replace() instead of replaceAll() for TypeScript target compatibility
      uploadedParts.push({ partNumber, etag: etag.replace(/"/g, "") });
      uploadedBytes += chunk.size;

      // Update progress
      setProgress({
        bytesUploaded: uploadedBytes,
        totalBytes: selectedFile.size,
        percentage: (uploadedBytes / selectedFile.size) * 100,
      });
    }

    // Step 3: Complete multipart upload
    const completeEndpoint = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/admin/upload/multipart/complete`;
    const completeResponse = await fetchWithAuth(completeEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({
        uploadId,
        key,
        assetId,
        parts: uploadedParts,
      }),
    });

    if (!completeResponse.ok) {
      throw new Error("Failed to complete multipart replacement upload");
    }

    // Confirm upload completion
    await confirmUploadCompletion(assetId);

    // Note: Encoding must be triggered manually for replacements
    // Admin can review upload success before triggering encoding
  };

  /**
   * Single PUT upload for files < 1GB
   */
  const handleSinglePutUpload = async () => {
    if (!selectedFile) return;

    console.log("[VideoUploadZone] handleSinglePutUpload check:", {
      submissionId,
      mode,
      titleId,
      check: !submissionId && !(mode === "title" && titleId),
    });

    if (!submissionId && !(mode === "title" && titleId)) {
      console.error("[VideoUploadZone] Upload blocked: missing submissionId or titleId");
      setError("Missing submission or title ID. Please refresh and try again.");
      return;
    }

    const currentToken = localStorage.getItem("lumi-access-token");
    if (!currentToken) {
      setError("Please log in again");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const uploadEndpoint =
        mode === "title"
          ? `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/admin/upload/presigned-url`
          : `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/upload/presigned-url`;

      const requestBody =
        mode === "title"
          ? {
              titleId: titleId || submissionId,
              assetKind,
              fileName: selectedFile.name,
              fileSize: selectedFile.size,
              contentType: selectedFile.type,
              // Include client-side extracted metadata for backend validation
              clientMetadata: videoMetadata ?? undefined,
            }
          : {
              submissionId,
              assetKind,
              fileName: selectedFile.name,
              fileSize: selectedFile.size,
              contentType: selectedFile.type,
              // Include client-side extracted metadata for backend validation
              clientMetadata: videoMetadata ?? undefined,
            };

      const presignedResponse = await fetchWithAuth(uploadEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!presignedResponse.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, assetId } = (await presignedResponse.json()) as {
        uploadUrl: string;
        assetId: string;
      };

      // Upload to R2 with progress
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            setProgress({
              bytesUploaded: e.loaded,
              totalBytes: e.total,
              percentage: (e.loaded / e.total) * 100,
            });
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || xhr.status === 204) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed"));
        });

        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader("Content-Type", selectedFile.type);
        xhr.send(selectedFile);
      });

      await confirmUploadCompletion(assetId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setUploading(false);
      setProgress(null);
    }
  };

  /**
   * Create multipart upload session
   */
  const createMultipartUpload = async (
    token: string,
    totalParts: number,
  ): Promise<{ uploadId: string; assetId: string; partUrls: MultipartPart[]; key: string }> => {
    const createEndpoint =
      mode === "title"
        ? `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/admin/upload/multipart/create`
        : `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/upload/multipart/create`;

    const requestBody =
      mode === "title"
        ? {
            titleId: titleId || submissionId,
            assetKind,
            fileName: selectedFile?.name,
            fileSize: selectedFile?.size,
            contentType: selectedFile?.type,
            totalParts,
            // Include client-side extracted metadata for backend validation
            clientMetadata: videoMetadata ?? undefined,
          }
        : {
            submissionId,
            assetKind,
            fileName: selectedFile?.name,
            fileSize: selectedFile?.size,
            contentType: selectedFile?.type,
            partCount: totalParts,
            // Include client-side extracted metadata for backend validation
            clientMetadata: videoMetadata ?? undefined,
          };

    const response = await fetchWithAuth(createEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error("Failed to create multipart upload");
    }

    return (await response.json()) as {
      uploadId: string;
      assetId: string;
      partUrls: MultipartPart[];
      key: string;
    };
  };

  /**
   * Upload all parts with concurrency control
   */
  const uploadAllParts = async (partUrls: MultipartPart[]): Promise<MultipartPart[]> => {
    if (!selectedFile) throw new Error("No file selected");

    const uploadedParts: MultipartPart[] = [];
    let totalUploaded = 0;
    let currentPartIndex = 0;
    const totalParts = partUrls.length;

    // Helper function to upload a single part
    const uploadPart = async (partIndex: number): Promise<void> => {
      const part = partUrls[partIndex];
      const start = partIndex * PART_SIZE;
      const end = Math.min(start + PART_SIZE, selectedFile.size);
      const chunk = selectedFile.slice(start, end);

      const response = await fetch(part.uploadUrl, {
        method: "PUT",
        body: chunk,
        headers: { "Content-Type": selectedFile.type },
      });

      if (!response.ok) {
        throw new Error(`Part ${part.partNumber} upload failed`);
      }

      const etag = response.headers.get("ETag");
      if (!etag) {
        throw new Error(`No ETag for part ${part.partNumber}`);
      }

      uploadedParts.push({
        partNumber: part.partNumber,
        uploadUrl: part.uploadUrl,
        etag: etag.split('"').join(""),
      });

      totalUploaded += chunk.size;
      setProgress({
        bytesUploaded: totalUploaded,
        totalBytes: selectedFile.size,
        percentage: (totalUploaded / selectedFile.size) * 100,
      });
    };

    // Upload parts with concurrency control
    const uploadPromises: Promise<void>[] = [];

    while (currentPartIndex < totalParts || uploadPromises.length > 0) {
      // Fill up to CONCURRENT_UPLOADS simultaneous uploads
      while (uploadPromises.length < CONCURRENT_UPLOADS && currentPartIndex < totalParts) {
        const partIndex = currentPartIndex;
        currentPartIndex++;
        uploadPromises.push(uploadPart(partIndex));
      }

      // Wait for at least one upload to complete
      if (uploadPromises.length > 0) {
        await Promise.race(uploadPromises);
        // Remove completed promises
        const settled = await Promise.allSettled(uploadPromises);
        uploadPromises.length = 0;
        for (const result of settled) {
          if (result.status === "rejected") {
            throw new Error(`Upload failed: ${result.reason}`);
          }
        }
      }
    }

    return uploadedParts;
  };

  /**
   * Complete multipart upload
   */
  const completeMultipartUpload = async (
    uploadId: string,
    assetId: string,
    key: string,
    parts: MultipartPart[],
  ): Promise<void> => {
    // Refresh token before completing (upload may have taken > 15min)
    const refreshSuccess = await refreshAuth();
    if (!refreshSuccess) {
      throw new Error("Failed to refresh authentication token");
    }

    const freshToken = localStorage.getItem("lumi-access-token");
    if (!freshToken) {
      throw new Error("No access token available after refresh");
    }

    const completeEndpoint =
      mode === "title"
        ? `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/admin/upload/multipart/complete`
        : `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/upload/multipart/complete`;

    const response = await fetchWithAuth(completeEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${freshToken}`,
      },
      body: JSON.stringify({
        uploadId,
        assetId,
        key,
        parts: parts.map((p) => ({
          partNumber: p.partNumber,
          etag: p.etag,
        })),
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to complete multipart upload");
    }
  };

  /**
   * Handle multipart upload for large files (≥1GB)
   * Orchestrates the multipart upload process
   */
  const handleMultipartUpload = async () => {
    if (!selectedFile) return;

    console.log("[VideoUploadZone] handleMultipartUpload check:", {
      submissionId,
      mode,
      titleId,
      check: !submissionId && !(mode === "title" && titleId),
    });

    if (!submissionId && !(mode === "title" && titleId)) {
      console.error("[VideoUploadZone] Upload blocked: missing submissionId or titleId");
      setError("Missing submission or title ID. Please refresh and try again.");
      return;
    }

    const currentToken = localStorage.getItem("lumi-access-token");
    if (!currentToken) {
      setError("Please log in again");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const totalParts = Math.ceil(selectedFile.size / PART_SIZE);

      // Step 1: Create multipart upload
      const { uploadId, assetId, partUrls, key } = await createMultipartUpload(currentToken, totalParts);

      // Step 2: Upload all parts with concurrency control
      const uploadedParts = await uploadAllParts(partUrls);

      // Step 3: Complete multipart upload
      await completeMultipartUpload(uploadId, assetId, key, uploadedParts);

      // Step 4: Confirm upload completion
      await confirmUploadCompletion(assetId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Multipart upload failed");
      setUploading(false);
      setProgress(null);
    }
  };

  const confirmUploadCompletion = async (assetId: string) => {
    const currentToken = localStorage.getItem("lumi-access-token");
    if (!currentToken) return;

    try {
      const completionEndpoint =
        mode === "title"
          ? `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/admin/upload/${assetId}/complete`
          : `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/upload/${assetId}/complete`;

      const response = await fetchWithAuth(completionEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload completion failed:", errorText);
        throw new Error("Failed to confirm upload");
      }

      const responseData = (await response.json()) as unknown;
      console.log("Upload completion response:", responseData);

      const { asset } = responseData as {
        asset: {
          rawVideoUrl?: string | null;
          hlsUrl?: string | null;
          id: string;
        };
      };

      const videoUrl = mode === "title" ? asset.hlsUrl || `pending:${asset.id}` : asset.rawVideoUrl || "";

      if (!videoUrl && mode === "submission") {
        throw new Error("No video URL returned from server");
      }

      console.log("Calling onChange with:", asset.id, videoUrl);
      onChange(asset.id, videoUrl);
      setUploading(false);
      setProgress(null);
      setSelectedFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to confirm upload");
      setUploading(false);
      setProgress(null);
    }
  };

  console.log(`[VideoUploadZone] ${label}:`, {
    value,
    assetKind,
    uploading,
    mode,
    isPending: value?.startsWith("pending:"),
    fileSize: selectedFile?.size,
    useMultipart: selectedFile && selectedFile.size >= MULTIPART_THRESHOLD,
  });

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm">{label}</Label>
        <p className="text-xs text-neutral-500 mt-1">
          Max size: {maxSizeGB}GB • Formats: MP4, MOV, MKV
          {selectedFile && selectedFile.size >= MULTIPART_THRESHOLD && (
            <span className="ml-2 text-blue-400">• Using multipart upload</span>
          )}
        </p>
      </div>

      {/* Upload completed */}
      {value && !uploading && !isReplacing && (
        <div
          className={`${
            value.startsWith("pending:") ? "bg-blue-900/20 border-blue-700" : "bg-green-900/0 border-green-700/0"
          } border rounded-lg p-4`}
        >
          <div className="flex items-center gap-3">
            <svg
              className={`w-6 h-6 ${value.startsWith("pending:") ? "text-blue-500" : "text-green-500"} flex-shrink-0`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={value.startsWith("pending:") ? "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" : "M5 13l4 4L19 7"}
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">
                {value.startsWith("pending:") ? "Upload Complete - Encoding Pending" : "Upload Complete"}
              </p>
              <p className="text-xs text-neutral-400">
                {value.startsWith("pending:")
                  ? "Video will be available after encoding completes"
                  : "Video uploaded successfully"}
              </p>
            </div>
            {canReplace && (
              <button
                type="button"
                onClick={() => setShowReplaceModal(true)}
                className="text-sm border border-neutral-700 rounded-md bg-transparent text-white hover:bg-amber-900/20 hover:text-lumi-accent hover:border-amber-400/60 focus-visible:text-lumi-accent focus-visible:shadow-none hover:shadow-none px-6 py-2"
              >
                Replace Video
              </button>
            )}
          </div>
        </div>
      )}

      {/* Replacement Modal */}
      {showReplaceModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg max-w-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-white">Replace Video</h3>
            <div className="bg-yellow-900/20 border border-yellow-700 rounded p-3">
              <p className="text-sm text-yellow-400 mb-2 font-semibold">⚠️ Important</p>
              <ul className="text-sm text-yellow-400/90 space-y-1 list-disc list-inside">
                <li>User watch progress will be preserved</li>
                <li>Video will be unavailable during encoding (~5-15 minutes)</li>
                <li>Existing video will be overwritten</li>
              </ul>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowReplaceModal(false)}
                className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsReplacing(true);
                  setShowReplaceModal(false);
                }}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-black text-sm font-semibold rounded transition-colors"
              >
                Continue to Replace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File selection */}
      {!value && !selectedFile && !uploading && (
        <div className="relative">
          <input
            type="file"
            accept="video/*"
            onChange={(e) => void handleFileSelect(e)}
            className="hidden"
            id={`video-${assetKind}`}
          />
          <label
            htmlFor={`video-${assetKind}`}
            className="flex flex-col items-center justify-center w-full h-40 border-2 border-neutral-900 rounded-lg cursor-pointer hover:border-lumi-accent/60 bg-neutral-900/20 hover:bg-lumi-accent/10 transition-colors duration-300"
          >
            <svg className="w-12 h-12 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="mt-2 text-sm text-neutral-400">Click to select video file</p>
            <p className="text-xs text-neutral-500">or drag and drop</p>
          </label>
        </div>
      )}

      {/* Replacement mode: file selection */}
      {isReplacing && !selectedFile && !uploading && (
        <div className="relative">
          <input
            type="file"
            accept="video/*"
            onChange={(e) => void handleFileSelect(e)}
            className="hidden"
            id={`video-replace-${assetKind}`}
          />
          <label
            htmlFor={`video-replace-${assetKind}`}
            className="flex flex-col items-center justify-center w-full h-40 border-2 border-yellow-700/50 rounded-lg cursor-pointer hover:border-yellow-600 bg-yellow-900/10 hover:bg-yellow-900/20 transition-colors duration-300"
          >
            <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="mt-2 text-sm text-yellow-400 font-medium">Select replacement video</p>
            <p className="text-xs text-yellow-500/70">This will replace the existing video</p>
          </label>
          <button
            type="button"
            onClick={() => {
              setIsReplacing(false);
              setSelectedFile(null);
              setError(null);
            }}
            className="mt-2 text-sm text-neutral-400 hover:text-white"
          >
            Cancel Replacement
          </button>
        </div>
      )}

      {/* File selected (normal upload or replacement) */}
      {selectedFile && !uploading && (!value || isReplacing) && (
        <>
          <VideoFilePreview
            file={selectedFile}
            submissionId={submissionId}
            titleId={titleId}
            onRemove={() => {
              setSelectedFile(null);
              setError(null);
              setVideoMetadata(null);
              setValidation(null);
              if (isReplacing) {
                setIsReplacing(false);
              }
            }}
            onUpload={() => void handleUpload()}
            formatBytes={formatBytes}
          />
          {/* Video validation warnings (Phase 5B) */}
          <VideoValidationWarnings metadata={videoMetadata} validation={validation} />
        </>
      )}

      {/* Uploading */}
      {uploading && progress && <VideoUploadProgress progress={progress} formatBytes={formatBytes} />}

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}
    </div>
  );
}
